import {
  deleteRestrictedCathrinUser,
  editRestrictedCathrinUser,
  addRestrictedCathrinUser,
} from "@/app/lib/actions/restricted-cathrin-users";
import { ItemContent, ItemTitle } from "@/components/ui/item";
import {
  ItemsListCard,
  ItemsListCardSkeleton,
} from "@/components/backoffice/items-list-card";
import {
  useRestrictedCathrinUsersQuery,
  invalidateRestrictedCathrinUsersQuery,
} from "@/lib/queries";
import { useQueryClient } from "@tanstack/react-query";
import * as z from "zod";

import {
  addRestrictedCathrinUserSchema,
  editRestrictedCathrinUserSchema,
} from "@/lib/schemas/restricted-cathrin-users";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { functionality } from "@/prisma/generated/enums";

type RestrictedCathrinUser = {
  id: number;
  user: string;
  functionality: functionality;
  isActive: boolean;
};

function RestrictedCathrinUserItemsListCard() {
  const queryClient = useQueryClient();

  const restrictedCathrinUsersQuery = useRestrictedCathrinUsersQuery({
    sort: { user: "asc" },
  });
  const handleCreate = async (
    restrictedCathrinUser: z.infer<typeof addRestrictedCathrinUserSchema>,
  ) => {
    const response = await addRestrictedCathrinUser(restrictedCathrinUser);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Nouvel utilisateur restreint CATHRIN créé.");
      invalidateRestrictedCathrinUsersQuery(queryClient);
    }
  };

  const handleUpdate = async (
    restrictedCathrinUser: z.infer<typeof editRestrictedCathrinUserSchema>,
  ) => {
    const response = await editRestrictedCathrinUser(restrictedCathrinUser);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Utilisateur restreint CATHRIN modifié.");
      invalidateRestrictedCathrinUsersQuery(queryClient);
    }
  };

  const handleDelete = async (id: { id: number }) => {
    const response = await deleteRestrictedCathrinUser(id);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Utilisateur restreint CATHRIN supprimé.");
      invalidateRestrictedCathrinUsersQuery(queryClient);
    }
  };

  if (restrictedCathrinUsersQuery.isPending) return <ItemsListCardSkeleton />;
  const restrictedCathrinUsers =
    restrictedCathrinUsersQuery.data?.restrictedCathrinUsers ?? [];

  return (
    <ItemsListCard<RestrictedCathrinUser>
      title="Utilisateurs restreints CATHRIN"
      items={restrictedCathrinUsers}
      createSchema={addRestrictedCathrinUserSchema}
      editSchema={editRestrictedCathrinUserSchema}
      defaultCreateValues={{
        user: "",
        functionality: "CAT",
        isActive: true,
      }}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      renderItem={(item) => (
        <>
          <ItemContent>
            <ItemTitle>{item.user}</ItemTitle>
          </ItemContent>
        </>
      )}
      renderFormFields={(form) => (
        <FieldGroup>
          <form.Field name="user">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>
                    Identifiant utilisateur
                  </FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    maxLength={20}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>
          <form.Field name="functionality">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>Fonctionnalité</FieldLabel>
                  <Select
                    name={field.name}
                    value={field.state.value}
                    onValueChange={(value) =>
                      field.handleChange(value as functionality)
                    }
                  >
                    <SelectTrigger
                      className="w-[180px]"
                      aria-invalid={isInvalid}
                    >
                      <SelectValue placeholder="Sélectionner une fonctionnalité" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="CAT">CAT</SelectItem>
                        <SelectItem value="H">H</SelectItem>
                        <SelectItem value="RIN">RIN</SelectItem>
                        <SelectItem value="SYSTEM">SYSTEM</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>

          <form.Field name="isActive">
            {(field) => (
              <Field>
                <FieldLabel htmlFor={field.name}>Statut</FieldLabel>{" "}
                <Switch
                  name={field.name}
                  checked={field.state.value}
                  onCheckedChange={field.handleChange}
                />
              </Field>
            )}
          </form.Field>
        </FieldGroup>
      )}
    />
  );
}

export { RestrictedCathrinUserItemsListCard };
